import { useState } from "react";

import type { CartLoadMetadata } from "@cart-client/analytics/events/CartLoad";

function snapshotCartLoadMetadata(): CartLoadMetadata | null {
    if (typeof window === "undefined") {
        return null;
    }

    const headers = window.__headers__;
    const prefetchEnabled = headers?.cartpageprefetch === "true";

    const cartApiData = window.cartApiData;
    const source = cartApiData?.source === "prefetch" ? "prefetch" : "bau";
    // Gated on source === "prefetch" because __prefetchBridgeUsed__ is sticky (set once,
    // never cleared) while cartApiData.source IS cleared by useFetchCart after consumption.
    // Without the gate, back-navigation remounts would report bridge_used:true / source:"bau".
    const prefetchBridgeUsed = window.__prefetchBridgeUsed__ === "true" && cartApiData?.source === "prefetch";
    const bgRefreshNeeded = !!cartApiData?.needsBackgroundRefresh;
    const bgRefreshReason = cartApiData?.backgroundRefreshReason ?? null;

    // Timer info is sourced from __prefetchTimerInfo__ (set in every prefetch-eligible path,
    // including the active-timer BAU fallback where cartApiData is never set) so the subtype
    // breakdown survives even when the cart is not served from cache.
    const timerInfo = window.__prefetchTimerInfo__;
    const hasActiveTimers = !!(timerInfo?.hasActiveTimers ?? cartApiData?.hasActiveTimers);
    const hasCroutonTimer = !!timerInfo?.hasCroutonTimer;
    const hasFlashDealTimer = !!timerInfo?.hasFlashDealTimer;
    const hasRestaurantClosingTimer = !!timerInfo?.hasRestaurantClosingTimer;
    const hasMagicItemsTimer = !!timerInfo?.hasMagicItemsTimer;

    const prefetchTimestampRaw = cartApiData?.prefetchTimestamp;
    const prefetchTimestampParsed = prefetchTimestampRaw ? parseInt(prefetchTimestampRaw, 10) : NaN;
    const prefetchTimestamp = Number.isFinite(prefetchTimestampParsed) ? prefetchTimestampParsed : null;

    const cacheTtlMs = typeof cartApiData?.cacheTtlMs === "number" ? cartApiData.cacheTtlMs : null;

    const prefetchFallbackReason = window.__prefetchFallbackReason__ ?? null;
    let prefetchDecision = "bau";
    if (source === "prefetch") {
        prefetchDecision = bgRefreshNeeded ? "serve_cache_with_bg_refresh" : "serve_cache";
    } else if (prefetchFallbackReason) {
        prefetchDecision = `bau_due_to_${prefetchFallbackReason}`;
    }
    const isRemount = window.__cartImpressionFired__ === "true";

    return {
        prefetchEnabled,
        prefetchBridgeUsed,
        source,
        bgRefreshNeeded,
        bgRefreshReason,
        isRemount,
        prefetchFallbackReason,
        prefetchDecision,
        hasActiveTimers,
        hasCroutonTimer,
        hasFlashDealTimer,
        hasRestaurantClosingTimer,
        hasMagicItemsTimer,
        prefetchTimestamp,
        cacheTtlMs,
    };
}

const useCartLoadImpression = (): CartLoadMetadata | null => {
    const [metadata] = useState<CartLoadMetadata | null>(() => snapshotCartLoadMetadata());
    return metadata;
};

export default useCartLoadImpression;
